/**
 * Minimal logger + a tiny typed event emitter.
 *
 * `log` also mirrors entries onto `window.__SPACES_LOG__` so end-to-end browser
 * tests can assert on client behaviour (e.g. "the TV's iframe executeCommand
 * fired") by reading the page log instead of scraping the console. Never log PII —
 * only the random device id, per spot-client's logging rule.
 */

export interface LogEntry {
    t: number;
    level: 'log' | 'warn' | 'error';
    msg: string;
    data?: unknown;
}

declare global {
    interface Window {
        __SPACES_LOG__?: LogEntry[];
    }
}

function record(level: LogEntry['level'], msg: string, data?: unknown): void {
    const entry: LogEntry = { t: Date.now(), level, msg, data };

    if (typeof window !== 'undefined') {
        (window.__SPACES_LOG__ ??= []).push(entry);
    }

    const line = `[spaces] ${msg}`;

    if (level === 'error') {
        console.error(line, data ?? '');
    } else if (level === 'warn') {
        console.warn(line, data ?? '');
    } else {
        console.log(line, data ?? '');
    }
}

export const log = {
    log: (msg: string, data?: unknown): void => record('log', msg, data),
    warn: (msg: string, data?: unknown): void => record('warn', msg, data),
    error: (msg: string, data?: unknown): void => record('error', msg, data)
};

/** A tiny typed event emitter (replaces spot-client's common/emitter for our needs). */
export class Emitter<Events extends Record<keyof Events, unknown>> {
    private readonly handlers = new Map<keyof Events, Set<(payload: never) => void>>();

    on<K extends keyof Events>(event: K, handler: (payload: Events[K]) => void): () => void {
        const set = this.handlers.get(event) ?? new Set();

        set.add(handler as (payload: never) => void);
        this.handlers.set(event, set);

        return () => set.delete(handler as (payload: never) => void);
    }

    emit<K extends keyof Events>(event: K, payload: Events[K]): void {
        const set = this.handlers.get(event);

        if (!set) {
            return;
        }

        for (const handler of [...set]) {
            (handler as (p: Events[K]) => void)(payload);
        }
    }

    clear(): void {
        this.handlers.clear();
    }
}
